/**
 * @file Components/Sections/StatsCounter.jsx
 * @description Animated statistics counter section with scroll-triggered
 * number animation. Builds trust and social proof.
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MapPin, Users, Globe, Award, Star, Calendar } from 'lucide-react';

const stats = [
  { icon: MapPin, value: 250, suffix: '+', label: 'Tour Packages', color: 'text-blue-600 bg-blue-50' },
  { icon: Users, value: 15000, suffix: '+', label: 'Happy Travellers', color: 'text-emerald-600 bg-emerald-50' },
  { icon: Globe, value: 45, suffix: '+', label: 'Destinations', color: 'text-purple-600 bg-purple-50' },
  { icon: Award, value: 98, suffix: '%', label: 'Satisfaction Rate', color: 'text-amber-600 bg-amber-50' },
  { icon: Star, value: 4.9, suffix: '/5', label: 'Average Rating', decimal: true, color: 'text-pink-600 bg-pink-50' },
  { icon: Calendar, value: 12, suffix: '+', label: 'Years Experience', color: 'text-indigo-600 bg-indigo-50' },
];

const AnimatedNumber = ({ value, suffix, decimal, inView }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const duration = 2000;
    const steps = 60;
    const increment = value / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setCount(value);
        clearInterval(timer);
      } else {
        setCount(decimal ? parseFloat(current.toFixed(1)) : Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [inView, value, decimal]);

  return (
    <span className="tabular-nums">
      {decimal ? count.toFixed(1) : count.toLocaleString()}{suffix}
    </span>
  );
};

const StatsCounter = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section className="relative py-20 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#1a2463] via-[#243185] to-[#2d3d9e]" />
      <div className="absolute inset-0 opacity-5"
        style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />

      <div ref={ref} className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">Numbers That Speak</h2>
          <p className="text-white/60 text-lg max-w-xl mx-auto">
            Trusted by thousands of travellers across India
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
          {stats.map((stat, i) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="text-center group"
              >
                <div className={`w-14 h-14 rounded-2xl ${stat.color} flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <Icon size={24} />
                </div>
                <p className="text-3xl md:text-4xl font-black text-white mb-1">
                  <AnimatedNumber value={stat.value} suffix={stat.suffix} decimal={stat.decimal} inView={inView} />
                </p>
                <p className="text-white/50 text-sm font-medium">{stat.label}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default StatsCounter;
